import { EditIcon, DeleteIcon } from "@chakra-ui/icons";
import {
  Button,
  Flex,
  Heading,
  Spinner,
  Table,
  TableCaption,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIsAuthenticated } from "@azure/msal-react";
import {
  usePagination,
  withPagination,
} from "../../contexts/PaginationContext";
import {
  getCustomerList,
  CustomerModel,
  deleteCustomer,
} from "../../clients/BackOfficeApi";
import { Link } from "react-router-dom";
import { CreateCustomerModal } from "../../components/modals/CreateCustomerModal";
import { ActionButton } from "../../elements/ActionButton";
import { PaginationForm } from "../../elements/PaginationForm";
import { TableLoadingRow } from "../../elements/TableLoadingRow";

export const CustomerListPage = withPagination(
  { settingsKey: "customers" },
  () => {
    const [customers, setCustomers] = useState<CustomerModel[]>([]);
    const isAuthenticated = useIsAuthenticated();
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const { limit, offset } = usePagination();
    const [loadedOffset, setLoadedOffset] = useState(0);
    const [tableSpan, setTableSpan] = useState(1);

    const fetchCustomers = useCallback(
      async (signal?: AbortSignal, refetchAll = false) => {
        if (!isAuthenticated) return;

        setLoading(true);

        const query = refetchAll
          ? { Offset: 0, Limit: offset + limit }
          : { Offset: offset, Limit: limit };

        getCustomerList({
          query,
          signal,
          throwOnError: true,
        })
          .then(({ data }) => {
            if (signal?.aborted) return;

            const items = data?.items ?? [];

            const loadedItems = items.length;
            setLoadedOffset(query.Offset + loadedItems);
            setCustomers((prev) => {
              prev.splice(query.Offset, loadedItems, ...items);
              return prev.slice(0, query.Offset + loadedItems);
            });
            setTotal(data?.totalCount ?? 0);
          })
          .catch((e) => {
            if (signal?.aborted) return;

            throw e;
          })
          .finally(() => setLoading(false));
      },
      [offset, limit, isAuthenticated],
    );

    const moreRowsAvailable = useMemo(
      () => loadedOffset < total,
      [loadedOffset, total],
    );

    useEffect(() => {
      const abortController = new AbortController();

      fetchCustomers(abortController.signal);

      return () => abortController.abort("Component unmounted or re-rendered");
    }, [isAuthenticated, fetchCustomers]);

    const fetchDeleteCustomer = useCallback(
      (customerId: number) => {
        deleteCustomer({
          path: { customerId },
          throwOnError: true,
        }).then(() => fetchCustomers(undefined, true));
      },
      [fetchCustomers],
    );

    return (
      <>
        <Heading as="h1" mb="4">
          Opdrachtgevers
        </Heading>
        <CreateCustomerModal />
        <Table variant="striped" w="auto">
          <TableCaption placement="top" whiteSpace="nowrap">
            Opdrachtgevers ({total})
          </TableCaption>
          <Thead>
            <Tr ref={(el) => setTableSpan(el?.childElementCount ?? 1)}>
              <Th>Id</Th>
              <Th>Naam</Th>
              <Th>Aantal gekoppelde woningtypes</Th>
              <Th>Acties</Th>
            </Tr>
          </Thead>
          <Tbody>
            <TableLoadingRow colSpan={tableSpan} loading={loading && !moreRowsAvailable} />
            {!customers.length && !loading && (
              <Tr>
                <Td colSpan={tableSpan}>Geen opdrachtgevers gevonden</Td>
              </Tr>
            )}
            {customers.map((customer) => (
              <Tr key={customer.id}>
                <Td>{customer.id}</Td>
                <Td>{customer.name}</Td>
                <Td>{customer.assignedResidenceTypeIds.length}</Td>
                <Td>
                  <Flex direction="column" gap={2} minW={24}>
                    <Button
                      as={Link}
                      color="blue.500"
                      leftIcon={<EditIcon />}
                      mr={2}
                      to={customer.id.toString()}
                      variant="link"
                    >
                      Bewerken
                    </Button>
                    <ActionButton
                      color="red.500"
                      confirmDescription="Weet je zeker dat je deze opdrachtgever wilt verwijderen? Deze actie kan niet ongedaan gemaakt worden."
                      confirmTitle="Verwijderen bevestigen"
                      icon={<DeleteIcon />}
                      isDestructive
                      onClick={(confirmed: boolean) => {
                        if (!confirmed) return;
                        fetchDeleteCustomer(customer.id);
                      }}
                      title="Verwijderen"
                      variant="link"
                    />
                  </Flex>
                </Td>
              </Tr>
            ))}
            {moreRowsAvailable && (
              <Tr>
                <Td colSpan={tableSpan}>
                  <PaginationForm loading={loading} offset={loadedOffset} />
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </>
    );
  },
);
